import { GuestReservationDto, PlaceEvent, ProtectedServerPlaceEvent, SocketIoEvent } from 'gleblu-socket-api';
import Logger from 'js-logger';
import moment from 'moment-timezone';
import { io } from 'socket.io-client';

import { subscriptionCreated } from '@/store/actions/subscription/createSubscription';

import { authenticationRequired } from '../store/actions/authentication/authenticationRequired';
import { layoutsUpdated } from '../store/actions/layout/layoutsUpdated';
import { newRealTimeReservationRequest } from '../store/actions/reservation/realTimeReservation';
import { reservationsUpdated } from '../store/actions/reservation/reservationsUpdated';
import { contactUpdated } from '../store/actions/setting/contactUpdated';
import { facebookPageUpdated } from '../store/actions/setting/facebook/facebookPageUpdated';
import { hoursUpdated } from '../store/actions/setting/hoursUpdated';
import { profileUpdated } from '../store/actions/setting/profileUpdated';
import { reservationSettingUpdated } from '../store/actions/setting/reservationSettingUpdated';
import authenticationTokenSelector, { loggedInUserSelector } from '../store/selectors/authenticationSelectors';
import { store } from '../store/store';

const socket = io(import.meta.env.VITE_PLACE_SOCKET_URL, {
  secure: true,
  rejectUnauthorized: true,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 300000,
  randomizationFactor: 0.5,
  timeout: 20000,
});

socket.on(SocketIoEvent.connect, async () => {
  Logger.info('Socket io client connected!');
  const storeState = store.getState();
  const token = authenticationTokenSelector(storeState);
  const loggedInUser = loggedInUserSelector(storeState);
  if (token && loggedInUser) {
    await new Promise((resolve) => socket.emit(ProtectedServerPlaceEvent.joinRoom, token, { loggedInUser }, resolve));
  }
});

socket.on(SocketIoEvent.reconnect, async () => {
  Logger.info('Socket io client reconnected!');
});

socket.on(SocketIoEvent.error, (err) => {
  if (err === 'Authentication error') {
    store.dispatch(authenticationRequired());
  }
  Logger.error(`error: ${err}`);
});

socket.on(SocketIoEvent.connectError, (err) => {
  Logger.error(`connect_error: ${err}`);
});

socket.on(SocketIoEvent.connectTimeout, (err) => {
  Logger.error(`connect_timeout: ${err}`);
});

socket.on(SocketIoEvent.reconnectError, (err) => {
  Logger.error(`reconnect_error: ${err}`);
});

socket.on(SocketIoEvent.reconnectFailed, (err) => {
  Logger.error(`reconnect_failed: ${err}`);
});

socket.on(PlaceEvent.updatedFacebookPage, ({ placeUuid }) => {
  store.dispatch(facebookPageUpdated({ placeUuid, timestamp: moment() }));
});

socket.on(PlaceEvent.updatedLayouts, ({ placeUuid }) => {
  store.dispatch(layoutsUpdated({ placeUuid, timestamp: moment() }));
});

socket.on(PlaceEvent.updatedContact, ({ placeUuid }) => {
  store.dispatch(contactUpdated({ placeUuid, timestamp: moment() }));
});

socket.on(PlaceEvent.updatedHours, ({ placeUuid }) => {
  store.dispatch(hoursUpdated({ placeUuid, timestamp: moment() }));
});

socket.on(PlaceEvent.updatedPlaceProfile, ({ placeUuid }) => {
  store.dispatch(profileUpdated({ placeUuid, timestamp: moment() }));
});

socket.on(PlaceEvent.updatedReservationSetting, ({ placeUuid }) => {
  store.dispatch(reservationSettingUpdated({ placeUuid, timestamp: moment() }));
});

socket.on(PlaceEvent.updatedReservations, ({ placeUuid }) => {
  store.dispatch(reservationsUpdated({ placeUuid, timestamp: moment() }));
});

socket.on(PlaceEvent.updatedRealTimeReservations, (dto: GuestReservationDto) => {
  store.dispatch(newRealTimeReservationRequest(dto));
});

socket.on(PlaceEvent.receiveReservationRequest, (dto: GuestReservationDto) => {
  store.dispatch(newRealTimeReservationRequest(dto));
});

socket.on(PlaceEvent.subscriptionCreated, ({ placeUuid, feature }) => {
  store.dispatch(subscriptionCreated({ placeUuid, feature, timestamp: moment() }));
});

export default socket;
